import React, { useMemo, useState, useRef } from 'react';
import { Layers, Rows3 } from 'lucide-react';
import {
  type FilterFn,
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getGroupedRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  flexRender,
  type ColumnFiltersState,
  type SortingState,
  type GroupingState,
  type ExpandedState,
  type VisibilityState,
  type ColumnOrderState,
  type PaginationState,
  type ColumnSizingState,
  type ColumnPinningState,
} from '@tanstack/react-table';
import { DataTableProps, DataTableColumn } from './types';
import DataTableHeader from './components/DataTableHeader';
import DataTableBody from './components/DataTableBody';
import ColumnVisibility from './components/ColumnVisibility';
import MultiSort from './components/MultiSort';
import MultiFilter from './components/MultiFilter';
import GroupingControls from './components/GroupingControls';
import ExportButton from './components/ExportButton';
import DataTableLegend from './components/DataTableLegend';
import { multiSelect, createCompoundFilter } from './filters';

// Default minimum column width constant
const DEFAULT_MIN_COLUMN_WIDTH = 80;

// localStorage utility functions for column persistence
type StoredColumnSettings = {
  visibility?: VisibilityState;
  order?: string[];
  pinning?: ColumnPinningState;
  condensed?: boolean;
};

const getStoredColumnSettings = (tableId: string): StoredColumnSettings | null => {
  try {
    const stored = localStorage.getItem(`datatable_${tableId}_columns`);
    return stored ? (JSON.parse(stored) as StoredColumnSettings) : null;
  } catch (error) {
    console.warn('Failed to parse stored column settings:', error);
    return null;
  }
};

const saveColumnSettings = (
  tableId: string,
  visibility: VisibilityState,
  order: string[],
  pinning?: ColumnPinningState,
  condensed?: boolean,
) => {
  try {
    const settings: StoredColumnSettings = {
      visibility,
      order,
      pinning,
      condensed,
    };
    localStorage.setItem(`datatable_${tableId}_columns`, JSON.stringify(settings));
  } catch (error) {
    console.warn('Failed to save column settings:', error);
  }
};

export function DataTable<TData extends Record<string, any>>({
  data,
  columns,
  rowBgColor,
  legend,
  enableFiltering = false,
  enableSorting = false,
  enableMultiSort = false,
  enableGrouping = false,
  enableColumnVisibility = false,
  enableColumnResizing = false,
  enableColumnPinning = false,
  enableMultiFilter = false,
  enableMultiSelect = false,
  enableExport = false,
  stickyHeaders = false,
  enableVirtualization = false,
  virtualizationRowHeight = 52,
  virtualizationOverscan = 2,
  defaultColumnVisibility = {},
  onColumnVisibilityChange,
  defaultSorting = [],
  defaultColumnSizing = {},
  onColumnSizingChange,
  columnResizeMode = 'onChange',
  filterMode = 'internal',
  filterStyle = 'select',
  onFilterChange,
  onSortChange,
  groupBy = [],
  onGroupingChange,
  onExport,
  exportTransform,
  exportFilename,
  rowClickable = false,
  onRowClick,
  loading = false,
  emptyMessage = 'No data available',
  className = '',
  tableClassName = '',
  fullMinimumColumnWidth = DEFAULT_MIN_COLUMN_WIDTH,
  defaultFiltering,
  persistColumnSettings = false,
  tableId,
  groupingBarButtons,
  customToolbarButtons,
  useSteppedGroupingExpansion = false,
  defaultHideLeafRows,
  defaultCondensed = false,
}: DataTableProps<TData>) {
  // Validate that tableId is provided when persistence is enabled
  if (persistColumnSettings && !tableId) {
    throw new Error('tableId is required when persistColumnSettings is enabled');
  }

  // Initialize state - either from localStorage or defaults
  const getInitialColumnSettings = () => {
    if (persistColumnSettings && tableId) {
      const stored = getStoredColumnSettings(tableId);
      if (stored) {
        return {
          visibility: stored.visibility || defaultColumnVisibility,
          order: stored.order || [],
          pinning: stored.pinning || { left: [], right: [] },
          condensed: stored.condensed,
        };
      }
    }
    return {
      visibility: defaultColumnVisibility,
      order: [],
      pinning: { left: [], right: [] },
      condensed: undefined,
    };
  };

  const initialSettings = getInitialColumnSettings();

  const [rowSelection, setRowSelection] = React.useState({})
  const [sorting, setSorting] = useState<SortingState>(defaultSorting);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(defaultFiltering || []);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialSettings.visibility);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(initialSettings.order);
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(defaultColumnSizing);
  const [columnPinning, setColumnPinning] = useState<ColumnPinningState>(initialSettings.pinning);
  const [grouping, setGrouping] = useState<GroupingState>(groupBy);
  const [expanded, setExpanded] = useState<ExpandedState>({}); // Start with all groups collapsed
  const [hideLeafRows, setHideLeafRows] = useState(!!defaultHideLeafRows);
  const [condensed, setCondensed] = useState(initialSettings.condensed ?? defaultCondensed);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const handleSortingChange = (updater: any) => {
    const newSorting = typeof updater === 'function' ? updater(sorting) : updater;
    setSorting(newSorting);
    onSortChange?.(newSorting);
  };

  const handleColumnFiltersChange = (updater: any) => {
    const newFilters = typeof updater === 'function' ? updater(columnFilters) : updater;
    if (filterMode === 'internal') {
      setColumnFilters(newFilters);
    }
    onFilterChange?.(newFilters);
  };

  const handleColumnVisibilityChange = (updater: any) => {
    const newVisibility = typeof updater === 'function' ? updater(columnVisibility) : updater;
    setColumnVisibility(newVisibility);
    onColumnVisibilityChange?.(newVisibility);
  };

  const handleGroupingChange = (updater: any) => {
    const newGrouping = typeof updater === 'function' ? updater(grouping) : updater;
    setGrouping(newGrouping);
    onGroupingChange?.(newGrouping);
  };

  const handleColumnSizingChange = (updater: any) => {
    const newSizing = typeof updater === 'function' ? updater(columnSizing) : updater;
    setColumnSizing(newSizing);
    onColumnSizingChange?.(newSizing);
  };

  const handleSaveColumnSettings = (nextCondensed?: boolean) => {
    if (persistColumnSettings && tableId) {
      saveColumnSettings(tableId, columnVisibility, columnOrder, columnPinning, nextCondensed ?? condensed);
    }
  };

  const processedColumns = useMemo(() => {
    return columns.map(col => {
      const column = col as DataTableColumn<TData>;
      const processed = { ...column };
      if (column.enableFiltering !== false && enableFiltering) {
        if (column.filterFn) {
          processed.filterFn = column.filterFn;
        } else if (column.columnFilterType || column.filterDisplayFn) {
          // When a columnFilterType or filterDisplayFn is provided, use the compound filter
          // that handles both value-selection and advanced filter rules
          processed.filterFn = createCompoundFilter<TData>(
            column.filterDisplayFn,
            column.filterParseDateFn,
            column.filterDateTimezone || 'UTC',
          );
        } else {
          processed.filterFn = multiSelect;
        }
      }

      if (column.sortingFn) {
        processed.sortingFn = column.sortingFn;
      }

      // Always add column sizing properties for CSS constraints
      const effectiveMinWidth = condensed ? Math.min(fullMinimumColumnWidth, 60) : fullMinimumColumnWidth;
      if (condensed && column.size === undefined) {
        const colMin = column.minSize ?? effectiveMinWidth;
        processed.size = Math.max(120, colMin);
      } else if (column.size !== undefined) {
        processed.size = column.size;
      }
      // Apply minimum column width, using the larger of the column's minSize or the global minimum
      if (column.minSize !== undefined) {
        processed.minSize = Math.max(column.minSize, effectiveMinWidth);
      } else {
        processed.minSize = effectiveMinWidth;
      }
      if (column.maxSize !== undefined) processed.maxSize = column.maxSize;

      // Only add enableResizing if column resizing is enabled
      if (enableColumnResizing && column.enableResizing !== undefined) {
        processed.enableResizing = column.enableResizing;
      }

      return processed;
    });
  }, [columns, enableFiltering, enableColumnResizing, condensed]);

  const table = useReactTable({
    data,
    columns: processedColumns,
    state: {
      rowSelection,
      sorting,
      columnFilters: filterMode === 'internal' ? columnFilters : [],
      columnVisibility,
      columnOrder,
      columnSizing,
      columnPinning,
      grouping,
      expanded,
    },
    enableSorting,
    enableMultiSort,
    enableFilters: enableFiltering,
    enableGrouping,
    enableColumnResizing,
    enableColumnPinning,
    enableRowSelection: enableMultiSelect,
    onRowSelectionChange: setRowSelection,
    columnResizeMode: enableColumnResizing ? columnResizeMode : undefined,
    columnResizeDirection: 'ltr',
    defaultColumn: {
      size: condensed ? 120 : 180,
      minSize: condensed ? Math.min(fullMinimumColumnWidth, 60) : fullMinimumColumnWidth,
      maxSize: 600,
    },
    meta: {
      condensed,
    },
    onSortingChange: handleSortingChange,
    onColumnFiltersChange: handleColumnFiltersChange,
    onColumnVisibilityChange: handleColumnVisibilityChange,
    onColumnOrderChange: setColumnOrder,
    onColumnSizingChange: handleColumnSizingChange,
    onColumnPinningChange: setColumnPinning,
    onGroupingChange: handleGroupingChange,
    onExpandedChange: setExpanded,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getGroupedRowModel: enableGrouping ? getGroupedRowModel() : undefined,
    getExpandedRowModel: enableGrouping ? getExpandedRowModel() : undefined,
    getFacetedRowModel: enableFiltering ? getFacetedRowModel() : undefined,
    getFacetedUniqueValues: enableFiltering ? getFacetedUniqueValues() : undefined,
  });

  const handleExport = () => {
    if (!onExport) return;

    const visibleRows = table.getFilteredRowModel().rows;
    const exportData = visibleRows.map(row => row.original);
    const transformedData = exportTransform ? exportTransform(exportData) : exportData;
    onExport(transformedData);
  };

  const gridTemplateColumns = table
    .getVisibleLeafColumns()
    .map(col => `${col.getSize()}px`)
    .join(' ');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className={`w-full ${className}`}>
      {(enableColumnVisibility || (enableSorting && enableMultiSort) || (enableFiltering && enableMultiFilter) || enableGrouping || enableExport || customToolbarButtons || legend) && (
        <div className="mb-4 flex flex-col lg:flex-row lg:items-center gap-2">
          {enableColumnVisibility && (
            <ColumnVisibility
              table={table}
              enableUserControl={enableColumnVisibility}
              enableColumnPinning={enableColumnPinning}
              onClose={handleSaveColumnSettings}
            />
          )}
          {enableSorting && enableMultiSort && (
            <MultiSort table={table} />
          )}
          {enableFiltering && enableMultiFilter && (
            <MultiFilter table={table} filterStyle={filterStyle} />
          )}
          {enableGrouping && (
            <GroupingControls table={table} groupingBarButtons={groupingBarButtons} />
          )}
          {enableGrouping && grouping.length > 1 && (
            <button
              onClick={() => setHideLeafRows(prev => !prev)}
              className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                hideLeafRows
                  ? 'text-white bg-blue-600 border-blue-600 hover:bg-blue-700'
                  : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
              }`}
              title={hideLeafRows ? 'Show leaf rows' : 'Hide leaf rows'}
              data-testid="groups-only-button"
            >
              <Layers className="w-4 h-4" />
              {hideLeafRows ? 'Groups Only' : 'Groups Only'}
            </button>
          )}
          {enableExport && (
            <ExportButton
              table={table}
              filename={exportFilename}
              onExport={onExport}
              exportTransform={exportTransform}
              hideLeafRows={hideLeafRows && grouping.length >= 2}
            />
          )}
          <button
            onClick={() => {
              const nextCondensed = !condensed;
              setCondensed(nextCondensed);
              handleSaveColumnSettings(nextCondensed);
            }}
            className={`inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
              condensed
                ? 'text-white bg-blue-600 border-blue-600 hover:bg-blue-700'
                : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
            }`}
            title={condensed ? 'Switch to normal view' : 'Switch to condensed view'}
            data-testid="condensed-button"
          >
            <Rows3 className="w-4 h-4" />
            Condensed
          </button>
          {legend && legend.length > 0 && (
            <DataTableLegend items={legend} />
          )}
          {customToolbarButtons && customToolbarButtons.map((buttonFn, index) => 
            buttonFn({
              setGrouping: handleGroupingChange,
              setColumnOrder: setColumnOrder,
              setColumnVisibility: handleColumnVisibilityChange,
              grouping,
              columnOrder,
              columnVisibility,
              table,
            })
          )}
        </div>
      )}
      <div
        ref={scrollContainerRef}
        data-testid="datatable-scroll-container"
        className={`overflow-x-auto ${enableVirtualization ? 'overflow-y-auto max-h-[600px]' : stickyHeaders ? 'overflow-y-auto max-h-[600px]' : ''} border border-gray-200 rounded-lg ${tableClassName}`}
      >
        <DataTableHeader
          table={table}
          enableSorting={enableSorting}
          enableMultiSort={enableMultiSort}
          enableFiltering={enableFiltering}
          enableGrouping={enableGrouping}
          enableColumnResizing={enableColumnResizing}
          filterStyle={filterStyle}
          stickyHeaders={stickyHeaders}
          useSteppedGroupingExpansion={useSteppedGroupingExpansion}
          gridTemplateColumns={gridTemplateColumns}
          condensed={condensed}
        />
        <DataTableBody
          table={table}
          columns={columns}
          emptyMessage={emptyMessage}
          enableVirtualization={enableVirtualization}
          enableColumnResizing={enableColumnResizing}
          rowHeight={virtualizationRowHeight}
          overscan={virtualizationOverscan}
          parentRef={enableVirtualization ? scrollContainerRef : undefined}
          rowClickable={rowClickable}
          onRowClick={onRowClick}
          hideLeafRows={hideLeafRows && grouping.length >= 2}
          gridTemplateColumns={gridTemplateColumns}
          rowBgColor={rowBgColor}
          condensed={condensed}
        />
      </div>
    </div>
  );
}
